import { useState, useEffect, useCallback } from 'react';
import { fetchAdminModulos, adminRequest } from '../../../shared/services/api';
import { ALL_MODULES } from '../../../../config/modules';
import { useI18n } from '../../../../i18n';
import { useAuth } from '../../../../context/AuthContext';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import { AdminToggle } from '../components/AdminToggle';

// ═══════════════════════════════════════════════════════════════
// AdminModulos — Module activation/deactivation with toggles
// ═══════════════════════════════════════════════════════════════

interface ModuloState {
  code: string;
  name: string;
  description: string;
  activo: boolean;
}

const DISCIPLINE_MAP: Record<string, { discipline: string; color: string }> = {
  M1: { discipline: 'Structure', color: '#1A94BF' },
  M2: { discipline: 'Process', color: '#2F5496' },
  M3: { discipline: 'Process', color: '#2F5496' },
  M4: { discipline: 'Information', color: '#4472C4' },
  M5: { discipline: 'Information', color: '#4472C4' },
  M6: { discipline: 'Information', color: '#4472C4' },
  M7: { discipline: 'Compliance', color: '#16507D' },
  M8: { discipline: 'Tech', color: '#33BCE1' },
  M9: { discipline: 'Tech', color: '#33BCE1' },
  M10: { discipline: 'Tech', color: '#33BCE1' },
  M11: { discipline: 'Transversal', color: '#6B7280' },
  M12: { discipline: 'Tech', color: '#33BCE1' },
  M13: { discipline: 'Reputation', color: '#0E243E' },
  M14: { discipline: 'Tech', color: '#33BCE1' },
  M15: { discipline: 'Tech', color: '#33BCE1' },
  M16: { discipline: 'Tech', color: '#33BCE1' },
  M17: { discipline: 'Transversal', color: '#6B7280' },
  M18: { discipline: 'Transversal', color: '#6B7280' },
  M19: { discipline: 'Compliance', color: '#16507D' },
  M20: { discipline: 'Tech', color: '#33BCE1' },
};

/** Dependencias: activar X requiere Y activo */
const DEPENDENCIES: Record<string, string[]> = {
  M3: ['M2'], // Flow requiere Process
  M5: ['M4'], // Search requiere Docs
  M6: ['M4'], // Versions requiere Docs
  M10: ['M9'], // Assistant requiere AI API
  M16: ['M15'], // Requests requiere Sign
};

/** Dependencias inversas: desactivar X afecta a Y */
function getDependents(code: string): string[] {
  return Object.entries(DEPENDENCIES)
    .filter(([, deps]) => deps.includes(code))
    .map(([dependent]) => dependent);
}

export function AdminModulos() {
  const { t } = useI18n();
  const { isPlatformAdmin } = useAuth();
  const [modulos, setModulos] = useState<ModuloState[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'warning' } | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'warning' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const loadModulos = async () => {
    try {
      setLoading(true);
      const res = await fetchAdminModulos();
      setModulos(res.data);
    } catch {
      setModulos(
        ALL_MODULES.map((m) => ({
          code: m.code,
          name: m.name,
          description: m.description ?? '',
          activo: true,
        })),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadModulos();
  }, []);

  const toggleModule = async (code: string) => {
    if (!isPlatformAdmin) {
      showToast(t.admin.modulos.soloPlatformPuedeActivar, 'warning');
      return;
    }
    const mod = modulos.find((m) => m.code === code);
    if (!mod) return;

    const newActive = !mod.activo;

    // Check dependencies when activating
    if (newActive) {
      const deps = DEPENDENCIES[code] ?? [];
      const missingDeps = deps.filter((d) => !modulos.find((m) => m.code === d)?.activo);
      if (missingDeps.length > 0) {
        showToast(`${code} ${t.admin.modulos.requiere} ${missingDeps.join(', ')}`, 'warning');
        return;
      }
    }

    // Check dependents when deactivating
    if (!newActive) {
      const dependents = getDependents(code);
      const activeDependents = dependents.filter((d) => modulos.find((m) => m.code === d)?.activo);
      if (activeDependents.length > 0) {
        showToast(`${activeDependents.join(', ')} ${t.admin.modulos.dependeDe} ${code}`, 'warning');
      }
    }

    // Optimistic update
    setModulos((prev) => prev.map((m) => (m.code === code ? { ...m, activo: newActive } : m)));

    try {
      await adminRequest(`/modules/${code}/toggle`, {
        method: 'PUT',
        body: JSON.stringify({ active: newActive }),
      });
      const label = (t.admin.modulos.descriptions as Record<string, string>)[code] ?? mod.name;
      showToast(
        `${code} ${label} ${newActive ? t.admin.modulos.activado : t.admin.modulos.desactivado}`,
      );
    } catch {
      // Revert on error
      setModulos((prev) => prev.map((m) => (m.code === code ? { ...m, activo: !newActive } : m)));
      showToast('Error al actualizar módulo', 'warning');
    }
  };

  const activos = modulos.filter((m) => m.activo).length;
  const inactivos = modulos.filter((m) => !m.activo).length;

  return (
    <div className="space-y-6 relative">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 rounded-lg px-4 py-3 text-sm font-medium text-white shadow-lg transition-all ${
            toast.type === 'warning' ? 'bg-amber-500' : 'bg-emerald-600'
          }`}
        >
          {toast.message}
        </div>
      )}

      <div>
        <h2 className="text-lg font-semibold text-gray-900">{t.admin.modulos.titulo}</h2>
        <p className="text-sm text-gray-500">{t.admin.modulos.estadoModulos}</p>
      </div>

      {/* 0567: banner read-only para roles no-plataforma */}
      {!isPlatformAdmin && (
        <div
          style={{
            padding: '10px 16px',
            borderRadius: 8,
            fontSize: 13,
            backgroundColor: '#EFF6FF',
            color: '#1E40AF',
            border: '1px solid #BFDBFE',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4M12 8h.01" />
          </svg>
          <span>{t.admin.modulos.soloPlatformPuedeActivar}</span>
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-xs text-gray-500">{t.admin.modulos.total}</p>
          <p className="text-2xl font-bold text-[#1F3864]">{modulos.length}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-xs text-gray-500">{t.admin.modulos.activos}</p>
          <p className="text-2xl font-bold text-green-600">{activos}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-xs text-gray-500">{t.admin.modulos.inactivos}</p>
          <p className="text-2xl font-bold text-gray-400">{inactivos}</p>
        </div>
      </div>

      {/* Module cards */}
      {loading ? (
        <div className="text-center py-8">
          <LoadingSpinner text={t.admin.modulos.cargando} size="sm" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {modulos.map((mod) => {
            const disc = DISCIPLINE_MAP[mod.code];
            const moduleDef = ALL_MODULES.find((m) => m.code === mod.code);
            const isPrevisto =
              moduleDef && 'estado' in moduleDef && moduleDef.estado === 'PREVISTO';
            return (
              <div
                key={mod.code}
                className="bg-white rounded-lg border overflow-hidden transition-all duration-200"
                style={{
                  borderColor: isPrevisto
                    ? '#d1d5db'
                    : mod.activo
                      ? (disc?.color ?? '#e5e7eb')
                      : '#f3f4f6',
                  opacity: isPrevisto ? 0.65 : mod.activo ? 1 : 0.5,
                }}
              >
                {/* Color bar */}
                <div className="h-1.5" style={{ backgroundColor: disc?.color ?? '#999' }} />
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span
                        className="text-xs font-bold px-2 py-0.5 rounded text-white"
                        style={{ backgroundColor: disc?.color ?? '#999' }}
                      >
                        {mod.code}
                      </span>
                      <span className="font-semibold text-gray-900 text-sm">{mod.name}</span>
                    </div>
                    {isPrevisto ? (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '2px 10px',
                          borderRadius: '9999px',
                          fontSize: '11px',
                          fontWeight: 600,
                          color: '#92400e',
                          backgroundColor: '#fef3c7',
                          border: '1px solid #fde68a',
                        }}
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <polyline points="12 6 12 12 16 14" />
                        </svg>
                        {((t.admin.modulos as Record<string, unknown>).proximamente as string) ??
                          'Próximamente'}
                      </span>
                    ) : (
                      <AdminToggle
                        checked={mod.activo}
                        disabled={!isPlatformAdmin}
                        onChange={() => toggleModule(mod.code)}
                      />
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    {(t.admin.modulos.descriptions as Record<string, string>)[mod.code] ??
                      mod.description}
                  </p>
                  {disc && (
                    <p className="text-[10px] text-gray-400 mt-2">VALOR·IA {disc.discipline}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
